import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  getThemeClass,
  getThemeComponentClasses,
} from "@/src_new/shared/lib/themeComponentClasses";
import { Button } from "@/src_new/shared/components";
import AuthDeliveryOptions from "./AuthDeliveryOptions";
import AuthIdentifierField from "./AuthIdentifierField";
import OtpCodeInput from "./OtpCodeInput";
import { X } from "lucide-react";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendOtp?: (identifier: string, type: "email" | "phone") => void | Promise<void>;
  onVerifyOtp?: (identifier: string, otp: string, type: "email" | "phone") => void | Promise<void>;
  isLoading?: boolean;
  message?: string;
  messageType?: "success" | "error" | "info";
  title?: string;
  validationType?: "email" | "phone" | "auto";
  emailAddress?: string;
  phoneNumber?: string;
  renderInline?: boolean;
}

const INITIAL_OTP = Array(6).fill("");

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSendOtp,
  onVerifyOtp,
  isLoading = false,
  message,
  messageType = "info",
  title,
  validationType = "auto",
  emailAddress,
  phoneNumber,
  renderInline = false,
}) => {
  const themeClasses = getThemeComponentClasses();
  const cardClasses = themeClasses.card;
  const inputClasses = themeClasses.input;
  const buttonClasses = themeClasses.button;
  const messageClasses =
    messageType === "error"
      ? "border border-theme-danger-bright-soft bg-theme-danger-soft text-theme-danger-text-strong"
      : messageType === "success"
        ? "border border-theme-success-border-soft bg-theme-success-pale text-theme-success-text-strong"
        : "border border-theme-warning-bright-soft bg-theme-warning-pale text-theme-warning-text-strong";
  const [identifier, setIdentifier] = useState("");
  const [step, setStep] = useState<"identifier" | "otp">("identifier");
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(120);
  const [otpValues, setOtpValues] = useState<string[]>(INITIAL_OTP);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const hasEmailOption = Boolean(emailAddress?.trim());
  const hasPhoneOption = Boolean(phoneNumber?.trim());
  const hasPredefinedContacts = hasEmailOption || hasPhoneOption;

  const defaultMethod = useCallback((): "email" | "phone" => {
    if (validationType === "phone" && hasPhoneOption) return "phone";
    if (validationType === "email" && hasEmailOption) return "email";
    if (hasEmailOption) return "email";
    return "phone";
  }, [hasEmailOption, hasPhoneOption, validationType]);

  const [selectedMethod, setSelectedMethod] = useState<"email" | "phone">(defaultMethod());

  const inputType = useMemo(() => {
    if (validationType === "email") return "email";
    if (validationType === "phone") return "phone";
    if (identifier.includes("@")) return "email";
    if (/^\d+$/.test(identifier.replace(/\s+/g, ""))) return "phone";
    return "email";
  }, [identifier, validationType]);

  const activeMethod: "email" | "phone" =
    selectedMethod === "phone" && !hasPhoneOption
      ? defaultMethod()
      : selectedMethod === "email" && !hasEmailOption
        ? defaultMethod()
        : selectedMethod;

  const effectiveType: "email" | "phone" = hasPredefinedContacts ? activeMethod : inputType;

  const effectiveIdentifier = hasPredefinedContacts
    ? activeMethod === "email"
      ? emailAddress?.trim() || ""
      : phoneNumber?.trim() || ""
    : identifier;

  const maskEmail = useCallback((value: string) => {
    const [local, domain] = value.split("@");
    if (!local || !domain) return value;
    const domainParts = domain.split(".");
    const domainName = domainParts[0] || "";
    const suffix = domainParts.length > 1 ? `.${domainParts.slice(1).join(".")}` : "";
    const maskedLocal = local.length <= 2 ? `${local.charAt(0)}***` : `${local.slice(0, 2)}***`;
    const maskedDomain =
      domainName.length <= 1 ? `${domainName.charAt(0)}***` : `${domainName.charAt(0)}***`;
    return `${maskedLocal}@${maskedDomain}${suffix}`;
  }, []);

  const maskPhone = useCallback((value: string) => {
    const digits = value.replace(/\D/g, "");
    if (!digits) return value;
    const lastFour = digits.slice(-4);
    const starCount = Math.max(digits.length - 4, 4);
    return `${value.trim().startsWith("+") ? "+" : ""}${"*".repeat(starCount)}${lastFour}`;
  }, []);

  const validateIdentifier = useCallback((value: string, type: "email" | "phone"): boolean => {
    if (type === "email") {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
    return /^\d{10,15}$/.test(value.replace(/\s+/g, ""));
  }, []);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const handleSendOtp = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!validateIdentifier(effectiveIdentifier, effectiveType)) {
        setError(
          effectiveType === "email"
            ? "Please enter a valid email address."
            : "Please enter a valid phone number (10-15 digits)."
        );
        return;
      }
      onSendOtp?.(effectiveIdentifier, effectiveType);
      setStep("otp");
      setError("");
      setTimer(120);
    },
    [effectiveIdentifier, effectiveType, onSendOtp, validateIdentifier]
  );

  const handleVerifyOtp = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const otp = otpValues.join("");
      if (otp.length !== 6) {
        setError("Please enter the 6-digit OTP.");
        return;
      }
      onVerifyOtp?.(effectiveIdentifier, otp, effectiveType);
    },
    [effectiveIdentifier, effectiveType, onVerifyOtp, otpValues]
  );

  const handleResendOtp = useCallback(() => {
    onSendOtp?.(effectiveIdentifier, effectiveType);
    setTimer(120);
    setOtpValues(INITIAL_OTP);
    inputsRef.current[0]?.focus();
  }, [effectiveIdentifier, effectiveType, onSendOtp]);

  const handleClose = useCallback(() => {
    setStep("identifier");
    setIdentifier("");
    setOtpValues(INITIAL_OTP);
    setError("");
    setTimer(120);
    setSelectedMethod(defaultMethod());
    onClose();
  }, [defaultMethod, onClose]);

  const handleOtpChange = useCallback(
    (value: string, index: number) => {
      if (!/^[0-9]?$/.test(value)) return;
      const newOtp = [...otpValues];
      newOtp[index] = value;
      setOtpValues(newOtp);
      if (value && index < 5) {
        inputsRef.current[index + 1]?.focus();
      }
    },
    [otpValues]
  );

  const handleOtpKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === "Backspace" && !otpValues[index] && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    },
    [otpValues]
  );

  const handleOtpPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData("text").trim();
    if (!/^\d{6}$/.test(pasteData)) return;
    const digits = pasteData.split("");
    setOtpValues(digits);
    digits.forEach((digit, index) => {
      if (inputsRef.current[index]) {
        inputsRef.current[index]!.value = digit;
      }
    });
    inputsRef.current[5]?.focus();
  }, []);

  useEffect(() => {
    if (step === "otp") {
      inputsRef.current[0]?.focus();
    }
  }, [step]);

  useEffect(() => {
    if (step !== "otp" || timer === 0) return;
    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timer, step]);

  if (!isOpen) return null;

  const content = (
    <div
      className={`relative rounded-2xl shadow-xl pt-3 pb-6 px-6 w-full max-w-md mx-auto ${getThemeClass(cardClasses.bgColor, "bg-theme-surface")} ${cardClasses.base}`}
    >
      <div className="flex justify-between items-center mb-2 -mt-2">
        <h2 className="text-lg font-semibold text-theme-text">{title}</h2>
        <Button
          onClick={handleClose}
          variant="secondary"
          className="min-h-auto rounded-lg border-0 px-2 py-1 text-2xl text-theme-text-soft shadow-none hover:bg-theme-surface-soft hover:text-theme-text-muted"
        >
          <X size={20} />
        </Button>
      </div>

      {message && (
        <div className={`mb-4 rounded-lg px-4 py-3 text-sm ${messageClasses}`}>{message}</div>
      )}

      <form
        onSubmit={step === "identifier" ? handleSendOtp : handleVerifyOtp}
        className="space-y-4"
      >
        {hasPredefinedContacts ? (
          <AuthDeliveryOptions
            hasPhoneOption={hasPhoneOption}
            hasEmailOption={hasEmailOption}
            activeMethod={activeMethod}
            onChange={setSelectedMethod}
            disabled={step === "otp"}
            phoneLabel={`SMS OTP on ${maskPhone(phoneNumber || "")}`}
            emailLabel={`OTP on email ${maskEmail(emailAddress || "")}`}
          />
        ) : (
          <AuthIdentifierField
            label="Email or phone number"
            value={identifier}
            onChange={setIdentifier}
            type={inputType === "email" ? "email" : "tel"}
            disabled={step === "otp"}
            placeholder="Enter email or phone number"
            required
          />
        )}

        {step === "otp" && (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="text-sm font-semibold text-theme-text-muted">Enter OTP</label>
              {timer > 0 ? (
                <span className="text-xs text-theme-text-soft">Resend in {formatTime(timer)}</span>
              ) : (
                <Button
                  type="button"
                  onClick={handleResendOtp}
                  variant="secondary"
                  className="min-h-auto border-0 px-0 py-0 text-xs font-medium text-theme-info shadow-none hover:bg-transparent hover:underline"
                >
                  Resend OTP
                </Button>
              )}
            </div>

            <OtpCodeInput
              values={otpValues}
              inputsRef={inputsRef}
              onPaste={handleOtpPaste}
              onChange={handleOtpChange}
              onKeyDown={handleOtpKeyDown}
            />
          </div>
        )}

        {error && (
          <p className={`text-sm ${inputClasses.errorTextColor || "text-theme-danger-bright"}`}>
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={isLoading || (step === "otp" && otpValues.join("").length !== 6)}
          className={`w-full rounded-lg px-4 py-3 font-semibold disabled:opacity-50 ${buttonClasses.solidBgColor || "bg-theme-accent"} ${getThemeClass(buttonClasses.solidTextColor, "text-theme-text")} ${buttonClasses.solid} ${buttonClasses.base}`}
        >
          {isLoading ? "Loading..." : step === "identifier" ? "Send OTP" : "Verify OTP"}
        </Button>
      </form>
    </div>
  );

  if (renderInline) {
    return content;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-theme-text/50 backdrop-blur-sm" onClick={handleClose} />

      {content}
    </div>
  );
};

export default LoginModal;
